'use client';

import type { Theme } from '@parkingbase/ecommerce-api-dev';

import { useFindSessionContext } from '@/app/(public)/FindSessionContext';
import { useParkingValidationVisible } from '@/hooks/parking-validation/useParkingValidationVisible';
import { useLoginHref } from '@/hooks/useLoginHref';
import { useRouter } from '@/lib/app-router';
import { getCompanyBookingEnablement, isPayByPlateEnabled } from '@/lib/theme-utils';
import { ReservationMode } from '@/components/reserve/search-bar/SearchBar.types';
import { CITATION_ROUTES, MY_ACCOUNT_ROUTES, PUBLIC_ROUTES } from '@/routes/routes.constants';
import { useThemeStore } from '@/stores/theme.store';
import { useAuthStore } from '@/stores/user.store';
import { useLandingBlocks, useSitePageMeta } from './LandingBlocksContext';
import type { SiteAction } from './SitePage.types';

const SEARCH_SELECTOR = '[data-cy="home-search"]';

function scrollToSearch(): void {
  // `scrollIntoView` is absent in some embedded browsers and in jsdom; the focus / mode change
  // that accompanies it still works without it.
  document.querySelector<HTMLElement>(SEARCH_SELECTOR)?.scrollIntoView?.({
    behavior: 'smooth',
    block: 'center',
  });
}

export interface SiteActionVisibilityContext {
  theme: Theme | null | undefined;
  isSignedIn: boolean;
  /** `useParkingValidationVisible()`; only read for `VALIDATE`. */
  validationVisible: boolean;
}

/**
 * Whether `action` is offered for this company and viewer. The ONE gating rule, shared by
 * `useSiteActions` (tiles, CTAs) and the site header's ACTION links (PB-9032), so an item and a
 * tile with the same action can never disagree.
 *
 * ## Invariants:
 * - Pure: the same inputs always give the same answer. Gating is UX only; each destination keeps
 *   its own guard.
 */
export function isSiteActionVisible(
  action: SiteAction,
  { theme, isSignedIn, validationVisible }: SiteActionVisibilityContext
): boolean {
  switch (action) {
    case 'BOOK':
      return getCompanyBookingEnablement(theme).hourly;
    case 'MONTHLY':
      return getCompanyBookingEnablement(theme).monthly;
    case 'PAY_BY_ZONE':
      return isPayByPlateEnabled(theme);
    case 'PAY_CITATION':
      return theme?.enableCitations === true;
    case 'VALIDATE':
      return isSignedIn && validationVisible;
    case 'MY_ACCOUNT':
    case 'CONTACT_SALES':
    case 'URL':
      return true;
  }
}

export interface SiteActions {
  /** Whether the tile / CTA for `action` is shown for this company and viewer. */
  isVisible: (action: SiteAction) => boolean;
  /** Performs `action`. `URL` is a no-op: it is rendered as an anchor, not run. */
  run: (action: SiteAction) => void;
}

/**
 * Gating and navigation for QUICK_ACTIONS tiles and HERO CTAs: one hook so a tile and a CTA with
 * the same action can never disagree.
 *
 * ## Invariants:
 * - Gating is UX only. `/ct`, parking validation and `/myaccount` keep their own guards, so a
 *   visible tile never grants access.
 * - Every gate reads `Theme` (never Company): BOOK / MONTHLY from `getCompanyBookingEnablement`,
 *   PAY_BY_ZONE from `isPayByPlateEnabled`, PAY_CITATION from `theme.enableCitations`, VALIDATE
 *   from `useParkingValidationVisible` (guests never see it).
 * - PAY_BY_ZONE is shown to guests too (the tile is hidden only when pay-by-plate is off). A guest
 *   is sent to login, which returns to `/`; a signed-in user opens the Find-session modal, which
 *   the layout mounts only for signed-in users.
 * - BOOK / MONTHLY drive the page's own search panel only when that panel offers the mode
 *   (`SitePageMeta.searchModes`: hourly for BOOK, monthly for MONTHLY), and go to `/reserve`
 *   (`?mode=monthly`) otherwise, so an action never puts the panel into a mode it does not allow.
 *
 * @returns plain functions, re-created per render so they always read the latest props
 */
export function useSiteActions(): SiteActions {
  const router = useRouter();
  const theme = useThemeStore((state) => state.theme);
  const isSignedIn = useAuthStore((state) => !!state.user);
  const loginHref = useLoginHref();
  const findSession = useFindSessionContext();
  const validationVisible = useParkingValidationVisible();
  const { heroProps } = useLandingBlocks();
  const { searchModes } = useSitePageMeta();

  const isVisible = (action: SiteAction): boolean =>
    isSiteActionVisible(action, { theme, isSignedIn, validationVisible });

  const run = (action: SiteAction): void => {
    switch (action) {
      case 'BOOK':
        if (searchModes.includes(ReservationMode.hourly)) {
          heroProps.onDestinationClick();
          scrollToSearch();
        } else {
          router.push(MY_ACCOUNT_ROUTES.reserve);
        }
        return;
      case 'MONTHLY':
        if (searchModes.includes(ReservationMode.monthly)) {
          heroProps.onModeChange(ReservationMode.monthly);
          scrollToSearch();
        } else {
          router.push(`${MY_ACCOUNT_ROUTES.reserve}?mode=monthly`);
        }
        return;
      case 'PAY_BY_ZONE':
        if (isSignedIn) findSession.openModal();
        else router.push(loginHref);
        return;
      case 'PAY_CITATION':
        router.push(CITATION_ROUTES.lookup);
        return;
      case 'VALIDATE':
        router.push(MY_ACCOUNT_ROUTES.parkingValidation);
        return;
      case 'MY_ACCOUNT':
        router.push(isSignedIn ? MY_ACCOUNT_ROUTES.overview : loginHref);
        return;
      case 'CONTACT_SALES':
        router.push(PUBLIC_ROUTES.contact);
        return;
      case 'URL':
        return;
    }
  };

  return { isVisible, run };
}
